import { Component, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Router } from '@angular/router';
import { FormsModule } from '@angular/forms';
import { ButtonModule } from 'primeng/button';
import { CardModule } from 'primeng/card';
import { ToastModule } from 'primeng/toast';
import { MessageService, ConfirmationService } from 'primeng/api';
import { ConfirmDialogModule } from 'primeng/confirmdialog';
import { TagModule } from 'primeng/tag';
import { PaginatorModule } from 'primeng/paginator';
import { UserService } from '../../layout/service/user.service';

@Component({
    selector: 'app-list-video-material',
    standalone: true,
    imports: [
        CommonModule,
        FormsModule,
        ButtonModule,
        CardModule,
        ToastModule,
        ConfirmDialogModule,
        TagModule,
        PaginatorModule
    ],
    providers: [MessageService, ConfirmationService],
    styles: `
        .title-icon {
            width: 4rem;
            height: 4rem;
            background: linear-gradient(90deg, #2162DF 100%, #558BED 0%);
            border-radius: 1rem;
            display: flex;
            align-items: center;
            justify-content: center;
            color: white;
        }
    `,
    template: `
        <div class="max-w-7xl mx-auto px-6 py-8">
            <!-- Header with Add Button -->
            <div class="flex justify-between items-center mb-4">
                <div class="flex items-center">
                    <div class="title-icon mr-3">
                        <i class="pi pi-video" style="font-size: 2rem"></i>
                    </div>
                    <div>
                        <h1 class="text-3xl mb-0 font-bold text-gray-900">Video Materials</h1>
                        <p class="text-gray-600">Daftar konten video untuk pembelajaran OSCE</p>
                    </div>
                </div>
                <p-button
                    *ngIf="isAuthorizedUser"
                    (onClick)="createNew()"
                    severity="success"
                    icon="pi pi-plus"
                    label="Tambah Video"
                    size="large">
                </p-button>
            </div>

            <div class="rounded-xl shadow-sm mb-6">
                <div class="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                    <div class="relative">
                        <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="lucide lucide-search absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5">
                            <circle cx="11" cy="11" r="8"></circle>
                            <path d="m21 21-4.3-4.3"></path>
                        </svg>
                        <input
                            type="text"
                            placeholder="Cari video..."
                            [(ngModel)]="searchQuery"
                            (input)="filterVideos()"
                            class="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500">
                    </div>
                </div>
                
                <!-- Category Badges -->
                <div class="flex flex-wrap gap-2">
                    <button
                        (click)="selectCategory('')"
                        [class]="selectedCategory === '' ? 'px-4 py-2 rounded-full text-sm font-semibold transition bg-blue-600 text-white shadow-md' : 'px-4 py-2 rounded-full text-sm font-semibold transition bg-gray-100 text-gray-700 hover:bg-gray-200'">
                        <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="inline-block mr-1">
                            <polygon points="22 3 2 3 10 12.46 10 19 14 21 14 12.46 22 3"></polygon>
                        </svg>
                        Semua
                    </button>
                    <button
                        *ngFor="let category of categories"
                        (click)="selectCategory(category)"
                        [class]="selectedCategory === category ? 'px-4 py-2 rounded-full text-sm font-semibold transition bg-blue-600 text-white shadow-md' : 'px-4 py-2 rounded-full text-sm font-semibold transition bg-gray-100 text-gray-700 hover:bg-gray-200'">
                        {{ category }}
                    </button>
                </div>
            </div>

            <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                <div
                    *ngFor="let video of paginatedVideos"
                    class="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden hover:shadow-lg transition cursor-pointer group relative"
                    (click)="viewVideo(video)">
                    <div class="relative aspect-video">
                        <img *ngIf="video.thumbnail_url" [src]="video.thumbnail_url" [alt]="video.name" class="w-full h-full object-fill" loading="lazy">
                        <div *ngIf="!video.thumbnail_url" class="w-full h-full bg-gradient-to-br from-blue-500 to-blue-700 flex items-center justify-center">
                            <i class="pi pi-video" style="font-size: 4.5rem;color:white;"></i>
                        </div>
                        <!-- Play overlay for thumbnail -->
                        <div *ngIf="video.thumbnail_url" class="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-30 transition flex items-center justify-center">
                            <i class="pi pi-play text-white text-4xl opacity-80 group-hover:opacity-100 transition"></i>
                        </div>
                        <div class="absolute top-3 left-3 bg-white text-black px-3 py-1 rounded text-sm font-semibold">
                            {{ video.kategori }}
                        </div>
                        <div class="absolute bottom-3 right-3 bg-black bg-opacity-20 text-white px-3 py-1 rounded text-md font-semibold">
                            {{ formatDuration(video.duration) }}
                        </div>
                        <!-- Action Buttons -->
                        <div *ngIf="isAuthorizedUser" class="absolute top-3 right-3 opacity-0 group-hover:opacity-100 transition-opacity">
                            <div class="flex space-x-2">
                                <button
                                    (click)="editVideo($event, video)"
                                    class="bg-white bg-opacity-90 hover:bg-opacity-100 text-gray-700 p-2 rounded-full shadow-sm transition">
                                    <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                                        <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"></path>
                                        <path d="m18.5 2.5 3 3L12 15l-4 1 1-4 9.5-9.5z"></path>
                                    </svg>
                                </button>
                                <button
                                    (click)="deleteVideo($event, video)"
                                    class="bg-white bg-opacity-90 hover:bg-opacity-100 text-red-600 p-2 rounded-full shadow-sm transition">
                                    <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                                        <path d="M3 6h18"></path>
                                        <path d="M19 6v14c0 1-1 2-2 2H7c-1 0-2-1-2-2V6"></path>
                                        <path d="M8 6V4c0-1 1-2 2-2h4c1 0 2 1 2 2v2"></path>
                                    </svg>
                                </button>
                            </div>
                        </div>
                    </div>
                    <div class="p-4">
                        <h3 class="text-lg font-bold text-gray-800 mb-2 group-hover:text-blue-600 transition line-clamp-2">
                            {{ video.name }}
                        </h3>
                        <p class="text-sm text-gray-600 mb-3 line-clamp-2">
                            {{ video.description || 'Deskripsi video belum tersedia' }}
                        </p>
                        <div class="flex items-center gap-2 flex-wrap">
                            <span
                            class="px-2 py-1 rounded text-xs font-semibold"
                            [ngClass]="getLevelClass(video.level)">
                                <i class="pi pi-chart-bar me-2"></i>
                                {{ video.level }}
                            </span>
                        </div>
                    </div>
                </div>
            </div>

            <!-- Pagination -->
            <div *ngIf="totalRecords > rows" class="flex justify-center mt-8">
                <p-paginator
                    [first]="first"
                    [rows]="rows"
                    [totalRecords]="totalRecords"
                    [rowsPerPageOptions]="[6, 9, 12, 18]"
                    (onPageChange)="onPageChange($event)">
                </p-paginator>
            </div>

            <!-- Empty State -->
            <div *ngIf="filteredVideos.length === 0 && !loading" class="text-center py-12">
                <i class="pi pi-video text-4xl text-gray-400 mb-4 block"></i>
                <p class="text-gray-500">Tidak ada video yang ditemukan</p>
            </div>

            <!-- Loading State -->
            <div *ngIf="loading" class="text-center py-12">
                <i class="pi pi-spin pi-spinner text-2xl text-blue-600"></i>
                <p class="mt-2 text-gray-600">Memuat video...</p>
            </div>
        </div>

        <p-confirmDialog></p-confirmDialog>
        <p-toast></p-toast>
    `
})
export class ListVideoMaterial implements OnInit {
    videoMaterials: any[] = [];
    filteredVideos: any[] = [];
    paginatedVideos: any[] = [];
    loading = false;
    searchQuery = '';
    selectedCategory = '';
    selectedLevel = '';
    isAuthorizedUser = false;
    categories: string[] = [];

    // Pagination properties
    first = 0;
    rows = 9; // 9 videos per page (3x3 grid)
    totalRecords = 0;

    constructor(
        private userService: UserService,
        private messageService: MessageService,
        private confirmationService: ConfirmationService,
        private router: Router
    ) { }

    ngOnInit() {
        this.checkUserAuthorization();
        this.loadVideoMaterials();
    }

    checkUserAuthorization() {
        try {
            const userData = localStorage.getItem('user');

            if (userData) {
                const user = JSON.parse(userData);
                console.log(user);
                this.isAuthorizedUser = user.ID_ACCESS_ROLE === 'N2FWBVCZUE' || user.ID_ACCESS_ROLE === '4AEJ1KAJTY';
            }
        } catch (error) {
            console.error('Error checking user authorization:', error);
            this.isAuthorizedUser = false;
        }
    }

    loadVideoMaterials() {
        this.loading = true;
        this.userService.getVideoMaterials().then(
            (response) => {
                this.videoMaterials = response.data || response || [];
                this.extractCategories();
                this.filteredVideos = [...this.videoMaterials];
                this.totalRecords = this.filteredVideos.length;
                this.updatePaginatedVideos();
                this.loading = false;
            },
            (error) => {
                this.messageService.add({
                    severity: 'error',
                    summary: 'Error',
                    detail: 'Gagal memuat data video materials'
                });
                this.loading = false;
            }
        );
    }

    filterVideos() {
        this.filteredVideos = this.videoMaterials.filter(video => {
            const matchesSearch = !this.searchQuery ||
                video.name.toLowerCase().includes(this.searchQuery.toLowerCase()) ||
                (video.description && video.description.toLowerCase().includes(this.searchQuery.toLowerCase()));

            const matchesCategory = !this.selectedCategory || video.kategori === this.selectedCategory;
            const matchesLevel = !this.selectedLevel || video.level === this.selectedLevel;

            return matchesSearch && matchesCategory && matchesLevel;
        });

        // Reset to first page when filtering
        this.first = 0;
        this.totalRecords = this.filteredVideos.length;
        this.updatePaginatedVideos();
    }

    selectCategory(category: string) {
        this.selectedCategory = category;
        this.filterVideos();
    }

    extractCategories() {
        const categorySet = new Set<string>();
        this.videoMaterials.forEach(video => {
            if (video.kategori) {
                categorySet.add(video.kategori);
            }
        });
        this.categories = Array.from(categorySet).sort();
    }

    updatePaginatedVideos() {
        const start = this.first;
        const end = this.first + this.rows;
        this.paginatedVideos = this.filteredVideos.slice(start, end);
    }

    onPageChange(event: any) {
        this.first = event.first;
        this.rows = event.rows;
        this.updatePaginatedVideos();
    }

    createNew() {
        this.router.navigate(['/app/video_material/create']);
    }

    viewVideo(video: any) {
        this.router.navigate(['/app/video_material/show', video.id]);
    }

    editVideo(event: Event, video: any) {
        event.stopPropagation();
        this.router.navigate(['/app/video_material/edit', video.id]);
    }

    deleteVideo(event: Event, video: any) {
        event.stopPropagation();
        this.confirmationService.confirm({
            message: `Apakah Anda yakin ingin menghapus video "${video.name}"?`,
            header: 'Konfirmasi Hapus',
            icon: 'pi pi-exclamation-triangle',
            acceptLabel: 'Ya, Hapus',
            rejectLabel: 'Batal',
            accept: () => {
                this.userService.deleteVideoMaterial({ id: video.id }).then(
                    () => {
                        this.messageService.add({
                            severity: 'success',
                            summary: 'Berhasil',
                            detail: 'Video material berhasil dihapus'
                        });
                        this.loadVideoMaterials();
                    },
                    (error) => {
                        this.messageService.add({
                            severity: 'error',
                            summary: 'Error',
                            detail: 'Gagal menghapus video material'
                        });
                    }
                );
            }
        });
    }

    getLevelClass(level: string): string {
        switch (level) {
            case 'UKNPDPD': return 'text-blue-700';
            case 'Preklinik': return 'text-green-700';
            default: return 'text-gray-700';
        }
    }

    formatDuration(seconds: number): string {
        const minutes = Math.floor(seconds / 60);
        const remainingSeconds = seconds % 60;
        return `${minutes}:${remainingSeconds.toString().padStart(2, '0')}`;
    }

    formatDate(dateString: string): string {
        if (!dateString) return '-';
        const date = new Date(dateString);
        return date.toLocaleDateString('id-ID', {
            day: '2-digit',
            month: '2-digit',
            year: 'numeric'
        });
    }
}